import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Button,
  Form,
  Input,
  Upload,
  message,
  Space,
  Tag,
  Descriptions,
  Avatar,
  Typography,
  Row,
  Col,
  Badge,
  Breadcrumb,
  Spin,
  Popconfirm
} from 'antd';
import {
  ArrowLeftOutlined,
  UploadOutlined,
  DownloadOutlined,
  PaperClipOutlined,
  LockOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  FileTextOutlined,
  HomeOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import API from '../services/api';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;

const UserTicketDetail = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyLoading, setReplyLoading] = useState(false);
  const [closeLoading, setCloseLoading] = useState(false);
  const [autoRefreshing, setAutoRefreshing] = useState(false);
  
  // 用于控制敏感信息的显示状态
  const [sensitiveVisible, setSensitiveVisible] = useState({});

  // 状态映射
  const statusMap = {
    open: { text: '待处理', color: 'orange' },
    in_progress: { text: '处理中', color: 'blue' },
    waiting_customer: { text: '等待客户', color: 'purple' },
    closed: { text: '已关闭', color: 'green' }
  };

  // 优先级映射
  const priorityMap = {
    low: { text: '低', color: 'green' },
    medium: { text: '中', color: 'orange' },
    high: { text: '高', color: 'red' },
    urgent: { text: '紧急', color: 'volcano' }
  };

  // 切换显示/隐藏敏感信息内容
  const toggleSensitive = (itemKey) => {
    setSensitiveVisible(prev => ({
      ...prev,
      [itemKey]: !prev[itemKey]
    }));
  };

  // 获取工单详情
  const fetchTicketDetail = useCallback(async (isAutoRefresh = false) => {
    if (isAutoRefresh) {
      setAutoRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      const response = await API.get(`/api/tickets/${ticketId}`);
      if (response.data.code === 0) {
        setTicket(response.data.data.ticket);
      } else {
        if (!isAutoRefresh) {
          message.error(response.data.msg);
          navigate('/user/tickets');
        }
      }
    } catch (error) {
      if (!isAutoRefresh) {
        message.error('获取工单详情失败: ' + error.message);
        navigate('/user/tickets');
      }
    }
    
    if (isAutoRefresh) {
      setAutoRefreshing(false);
    } else {
      setLoading(false);
    }
  }, [ticketId, navigate]);

  // 关闭工单
  const handleCloseTicket = async () => {
    setCloseLoading(true);
    try {
      const response = await API.post(`/api/tickets/${ticketId}/close`);
      if (response.data.code === 0) {
        message.success('工单已关闭');
        // 只更新工单数据，不显示加载状态
        fetchTicketDetail(true);
      } else {
        message.error(response.data.msg);
      }
    } catch (error) {
      message.error('关闭工单失败: ' + error.message);
    }
    setCloseLoading(false);
  };

  // 添加回复
  const handleAddReply = async (values) => {
    setReplyLoading(true);
    try {
      const formData = new FormData();
      formData.append('content', values.content);

      if (values.attachments && values.attachments.fileList) {
        values.attachments.fileList.forEach(file => {
          if (file.originFileObj) {
            formData.append('attachments', file.originFileObj);
          }
        });
      }

      const response = await API.post(`/api/tickets/${ticketId}/replies`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.code === 0) {
        message.success('回复添加成功');
        // 只更新工单数据，不显示加载状态
        fetchTicketDetail(true);
      } else {
        message.error(response.data.msg);
      }
    } catch (error) {
      message.error('添加回复失败: ' + error.message);
    }
    setReplyLoading(false);
  };

  // 下载附件
  const handleDownloadAttachment = async (filename, originalName) => {
    try {
      const response = await API.get(`/api/tickets/attachments/${filename}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', originalName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      message.error('下载失败: ' + error.message);
    }
  };

  useEffect(() => {
    fetchTicketDetail();
    
    // 设置1分钟自动刷新
    const interval = setInterval(() => {
      fetchTicketDetail(true); // 传入true表示是自动刷新
    }, 60000); // 60秒 = 60000毫秒
    
    // 清理定时器
    return () => {
      clearInterval(interval);
    };
  }, [fetchTicketDetail]);

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!ticket) {
    return null;
  }

  return (
    <div style={{ padding: '24px', background: '#f0f2f5', minHeight: 'calc(100vh - 64px)' }}>
      {/* 面包屑导航 */}
      <Breadcrumb style={{ marginBottom: '16px' }}>
        <Breadcrumb.Item href="/">
          <HomeOutlined />
        </Breadcrumb.Item>
        <Breadcrumb.Item href="/user/tickets">
          <FileTextOutlined />
          我的工单
        </Breadcrumb.Item>
        <Breadcrumb.Item>工单详情</Breadcrumb.Item>
      </Breadcrumb>

      {/* 页面标题和操作 */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/user/tickets')}
            style={{ marginRight: '16px' }}
          >
            返回
          </Button>
          <Title level={2} style={{ margin: 0 }}>
            工单详情 - {ticket.title}
            {autoRefreshing && (
              <span style={{ fontSize: '12px', color: '#1890ff', marginLeft: '8px' }}>
                正在同步...
              </span>
            )}
          </Title>
        </div>
      </div>

      <Row gutter={24}>
        {/* 左侧：主要内容 */}
        <Col span={24} xl={16}>
          <Card title="基本信息" style={{ marginBottom: '24px' }}>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="工单编号" span={2}>
                <Text copyable>{ticket._id}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag color={statusMap[ticket.status]?.color}>
                  {statusMap[ticket.status]?.text}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="优先级">
                <Tag color={priorityMap[ticket.priority]?.color}>
                  {priorityMap[ticket.priority]?.text}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="创建时间" span={2}>
                {dayjs(ticket.createdAt).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label="标题" span={2}>
                {ticket.title}
              </Descriptions.Item>
              <Descriptions.Item label="描述" span={2}>
                <Paragraph>{ticket.description}</Paragraph>
              </Descriptions.Item>
              {ticket.tags && ticket.tags.length > 0 && (
                <Descriptions.Item label="标签" span={2}>
                  {ticket.tags.map(tag => (
                    <Tag key={tag}>{tag}</Tag>
                  ))}
                </Descriptions.Item>
              )}
              {/* 敏感信息 */}
              {ticket.sensitiveData && ticket.sensitiveData.length > 0 && (
                <Descriptions.Item label={<><LockOutlined /> 敏感信息</>} span={2}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {ticket.sensitiveData.map(item => {
                      const itemKey = item._id || item.label;
                      const visible = sensitiveVisible[itemKey];
                      return (
                        <div key={itemKey} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          background: '#f5f5f5', 
                          borderRadius: '4px', 
                          padding: '4px 8px',
                          fontSize: '12px'
                        }}>
                          <Text strong style={{ marginRight: 4, fontSize: '12px' }}>{item.label}:</Text>
                          {item.error ? (
                            <Text type="danger" style={{ fontSize: '12px' }}>解密失败</Text>
                          ) : (
                            <>
                              <span style={{
                                fontFamily: 'monospace',
                                fontSize: '12px',
                                marginRight: 4
                              }}>
                                {visible ? item.value : '******'}
                              </span>
                              <Button
                                type="text"
                                size="small"
                                icon={visible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                                onClick={() => toggleSensitive(itemKey)}
                                style={{ 
                                  padding: '0 4px',
                                  height: '16px',
                                  fontSize: '10px',
                                  color: '#1890ff'
                                }}
                                title={visible ? '隐藏' : '点击查看'}
                              />
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>



          {/* 回复记录 */}
          <Card title="回复记录" className="mb-4">
            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {ticket.replies && ticket.replies.length > 0 ? (
                ticket.replies.map(reply => (
                  <div 
                    key={reply._id}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      marginBottom: '16px',
                      alignItems: reply.isStaff ? 'flex-start' : 'flex-end'
                    }}
                  >
                    <div 
                      style={{
                        display: 'flex',
                        flexDirection: reply.isStaff ? 'row' : 'row-reverse',
                        alignItems: 'center',
                        marginBottom: '4px'
                      }}
                    >
                      <Avatar style={{ background: reply.isStaff ? '#1890ff' : '#f56a00' }}>
                        {reply.isStaff ? '维' : reply.author?.username?.charAt(0)}
                      </Avatar>
                      <div style={{ marginLeft: reply.isStaff ? '8px' : 0, marginRight: reply.isStaff ? 0 : '8px' }}>
                        <Space>
                          <span style={{ fontWeight: 'bold' }}>{reply.isStaff ? '运维' : reply.author?.username}</span>
                          {reply.isStaff && <Tag color="blue">客服</Tag>}
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {dayjs(reply.createdAt).format('YYYY-MM-DD HH:mm:ss')}
                          </Text>
                        </Space>
                      </div>
                    </div>
                    <div 
                      style={{
                        maxWidth: '80%',
                        padding: '12px 16px',
                        background: reply.isStaff ? '#e6f7ff' : '#f5f5f5',
                        borderRadius: '8px',
                        alignSelf: reply.isStaff ? 'flex-start' : 'flex-end',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                      }}
                    >
                      <Paragraph style={{ margin: 0 }}>{reply.content}</Paragraph>
                      {reply.attachments && reply.attachments.length > 0 && (
                        <div style={{ marginTop: '8px', borderTop: '1px solid #eee', paddingTop: '8px' }}>
                          <Text strong style={{ display: 'block', marginBottom: '4px' }}>附件：</Text>
                          {reply.attachments.map(attachment => (
                            <Button
                              key={attachment.filename}
                              type="link"
                              size="small"
                              icon={<DownloadOutlined />}
                              onClick={() => handleDownloadAttachment(attachment.filename, attachment.originalName)}
                            >
                              {attachment.originalName}
                            </Button>
                          ))}
                        </div>
                      )}
                      {/* 敏感信息适配显示 */}
                      {reply.sensitiveData && reply.sensitiveData.length > 0 && (
                        <div style={{ marginTop: 8 }}>
                          <Text strong>敏感信息：</Text>
                          {reply.sensitiveData.map(item => {
                            const itemKey = item._id || item.label;
                            const uniqueKey = reply._id + '_' + itemKey;
                            const visible = sensitiveVisible[uniqueKey];
                            return (
                              <div key={itemKey} style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                                <Text style={{ marginRight: 8 }}>{item.label}：</Text>
                                {item.error ? (
                                  <Text type="danger">解密失败</Text>
                                ) : (
                                  visible ? (
                                    <>
                                      <Text code style={{ marginRight: 8 }}>{item.value}</Text>
                                      <Button type="link" size="small" onClick={() => toggleSensitive(uniqueKey)}>隐藏</Button>
                                    </>
                                  ) : (
                                    <Button type="link" size="small" onClick={() => toggleSensitive(uniqueKey)}>点击查看</Button>
                                  )
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <Text type="secondary">暂无回复</Text>
              )}
            </div>
            
                         {/* 添加回复输入框 - 只有当工单未关闭时才显示 */}
             {ticket.status !== 'closed' && (
               <div className="mt-4 pt-4 border-t">
                 <Form onFinish={handleAddReply} key={ticket.replies?.length || 0} layout="vertical">
                   <Form.Item
                     name="content"
                     label="回复内容"
                     rules={[{ required: true, message: '请输入回复内容' }]}
                   >
                     <Input.TextArea
                       rows={4}
                       placeholder="输入回复内容..."
                       style={{ resize: 'vertical' }}
                     />
                   </Form.Item>
                   <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                     <Form.Item
                       name="attachments"
                       label="附件"
                       style={{ flex: 1, marginBottom: 0 }}
                     >
                       <Upload
                         multiple
                         beforeUpload={() => false}
                         maxCount={5}
                       >
                         <Button icon={<UploadOutlined />}>选择文件</Button>
                       </Upload>
                     </Form.Item>
                     <Form.Item style={{ marginBottom: 0, marginLeft: '16px' }}>
                       <Button type="primary" htmlType="submit" loading={replyLoading}>
                         发送回复
                       </Button>
                     </Form.Item>
                   </div>
                 </Form>
               </div>
             )}
          </Card>
        </Col>

        {/* 右侧：工单状态和操作 */}
        <Col span={24} xl={8}>
          <Card title="工单状态" style={{ marginBottom: '16px' }}>
            <div className="space-y-2">
              <div>
                <Text type="secondary">当前状态：</Text>
                <Badge 
                  status={ticket.status === 'closed' ? 'success' : 'processing'} 
                  text={statusMap[ticket.status]?.text}
                />
              </div>
              <div>
                <Text type="secondary">创建于：</Text>
                <Text>{dayjs(ticket.createdAt).format('YYYY-MM-DD HH:mm:ss')}</Text>
              </div>
              <div>
                <Text type="secondary">更新于：</Text>
                <Text>{dayjs(ticket.updatedAt).format('YYYY-MM-DD HH:mm:ss')}</Text>
              </div>
              {ticket.lastCustomerReplyAt && (
                <div>
                  <Text type="secondary">最后回复于：</Text>
                  <Text>{dayjs(ticket.lastCustomerReplyAt).format('YYYY-MM-DD HH:mm:ss')}</Text>
                </div>
              )}
            </div>
          </Card>

          {ticket.status !== 'closed' && (
            <Card title="工单操作" style={{ marginBottom: '16px' }}>
              <Popconfirm
                title="确认关闭此工单吗？关闭后将无法继续回复。"
                onConfirm={handleCloseTicket}
                okText="确认"
                cancelText="取消"
              >
                <Button 
                  danger 
                  style={{ width: '100%' }}
                  loading={closeLoading}
                >
                  关闭工单
                </Button>
              </Popconfirm>
            </Card>
          )}

          <Card title="统计信息" style={{ marginBottom: '16px' }}>
            <div className="space-y-2">
              <div>
                <Text type="secondary">回复总数：</Text>
                <Text>{ticket.replies ? ticket.replies.length : 0}</Text>
              </div>
              <div>
                <Text type="secondary">附件数量：</Text>
                <Text>{ticket.attachments ? ticket.attachments.length : 0}</Text>
              </div>
            </div>
          </Card>

          {/* 附件 */}
          {ticket.attachments && ticket.attachments.length > 0 && (
            <Card title="附件">
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {ticket.attachments.map((attachment, index) => (
                  <div key={index} style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    padding: '8px 0',
                    borderBottom: index < ticket.attachments.length - 1 ? '1px solid #f0f0f0' : 'none'
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ 
                        fontSize: '13px', 
                        fontWeight: 'bold',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        <PaperClipOutlined style={{ marginRight: '4px', color: '#1890ff' }} />
                        {attachment.originalName}
                      </div>
                      <div style={{ fontSize: '11px', color: '#999' }}>
                        {(attachment.size / 1024).toFixed(2)} KB
                      </div>
                    </div>
                    <Button
                      type="link"
                      size="small"
                      icon={<DownloadOutlined />}
                      onClick={() => handleDownloadAttachment(attachment.filename, attachment.originalName)}
                      style={{ fontSize: '12px' }}
                    >
                      下载
                    </Button>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default UserTicketDetail; 